import React, { useEffect, useRef, useCallback, useState } from 'react';
import '../../detailscardSliders.css';

import { useTopicStore } from './store';
import ThinkingIndicator from '../../components/ThinkingIndicator';
import type { LocationTopic, NamedImageItem } from './types/detailsCard.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface TopSightsCarouselV2Props {
  onSightSelect?: (sight: NamedImageItem) => void;
  onSightShowOnMap?: (sight: NamedImageItem) => void;
  onSightFocus?: (sight: NamedImageItem | null) => void;
  onPointerDown?: (e: React.PointerEvent) => void;
  preferredLanguageCode?: string;
}

// ---------------------------------------------------------------------------
// Component  (render-only – fetching is triggered by the store actions)
// ---------------------------------------------------------------------------
export default function TopSightsCarouselV2({
  onSightSelect,
  onSightShowOnMap,
  onSightFocus,
  onPointerDown,
}: TopSightsCarouselV2Props) {
  // ---- Store selectors ---------------------------------------------------
  const activeTopic = useTopicStore((s) =>
    s.activeTopicId ? s.topics[s.activeTopicId] ?? null : null,
  );

  const isLocation = activeTopic?.type === 'location';
  const locationTopic = isLocation ? (activeTopic as LocationTopic) : null;
  const slider = locationTopic?.topSightsImages;
  const items = slider?.items ?? [];
  const isLoading = slider?.isLoading ?? false;

  const [armedSightKey, setArmedSightKey] = useState<string | null>(null);

  useEffect(() => {
    setArmedSightKey(null);
  }, [activeTopic?.id, items.length]);

  // ---- Horizontal pointer-drag scroll -----------------------------------
  const carouselScrollRef = useRef<HTMLDivElement>(null);

  const handlePointerDownForward = useCallback(
    (e: React.PointerEvent) => {
      if (onPointerDown) onPointerDown(e);
    },
    [onPointerDown],
  );

  useEffect(() => {
    const el = carouselScrollRef.current;
    if (!el) return;

    el.style.touchAction = 'pan-x';

    let isDragging = false;
    let mode: 'pending' | 'horizontal' | 'vertical' = 'pending';
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    const ADMIT_THRESHOLD = 8;

    const onPointerDownLocal = (e: PointerEvent) => {
      if (e.button && e.button !== 0) return;
      startX = e.clientX;
      startY = e.clientY;
      startScrollLeft = el.scrollLeft;
      isDragging = true;
      mode = 'pending';
    };

    const onPointerMoveLocal = (e: PointerEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (mode === 'pending') {
        if (Math.abs(dx) > ADMIT_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.1) {
          mode = 'horizontal';
        } else if (Math.abs(dy) > ADMIT_THRESHOLD && Math.abs(dy) > Math.abs(dx)) {
          mode = 'vertical';
          isDragging = false;
          return;
        } else {
          return;
        }
      }

      if (mode === 'horizontal') {
        el.scrollLeft = startScrollLeft - dx;
        e.preventDefault();
        e.stopPropagation();
        try {
          el.setPointerCapture(e.pointerId);
        } catch {}
      }
    };

    const endDrag = (e: PointerEvent) => {
      isDragging = false;
      mode = 'pending';
      if (e?.pointerId !== undefined) {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {}
      }
    };

    el.addEventListener('pointerdown', onPointerDownLocal);
    el.addEventListener('pointermove', onPointerMoveLocal);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);

    return () => {
      el.removeEventListener('pointerdown', onPointerDownLocal);
      el.removeEventListener('pointermove', onPointerMoveLocal);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
    };
  }, []);

  const getSightKey = useCallback((item: NamedImageItem, index: number) => {
    return String(item.id || item.link || `${item.label || 'sight'}-${index}`);
  }, []);

  const handleSightCardClick = useCallback((item: NamedImageItem, sightKey: string) => {
    if (armedSightKey === sightKey) {
      setArmedSightKey(null);
      onSightSelect?.(item);
      return;
    }

    setArmedSightKey(sightKey);
    onSightFocus?.(item);
    onSightShowOnMap?.(item);
  }, [armedSightKey, onSightFocus, onSightSelect, onSightShowOnMap]);

  // ---- Render states -----------------------------------------------------

  // Not a location topic → render nothing
  if (!isLocation) return null;

  // Loading with no items yet
  if (isLoading && items.length === 0) {
    return (
      <ThinkingIndicator style={{ margin: '8px 0' }} />
    );
  }

  // Done loading but no results -> render nothing
  if (!isLoading && items.length === 0) {
    return null;
  }

  // ---- Card list ---------------------------------------------------------
  return (
    <div
      className="sights-carousel-container slider-container"
      onPointerDown={handlePointerDownForward}
    >
      <div ref={carouselScrollRef} className="sights-carousel slider-horizontal">
        {items.map((item, i) => {
          const key = getSightKey(item, i);
          const hasImage = !!item.imageUrl;
          const isEnriching = !!item.isEnriching;
          const isArmed = armedSightKey === key;

          return (
            <div
              key={key}
              className={`sight-card-carousel slider-card${isArmed ? ' sight-card-carousel--armed' : ''}`}
              style={{ cursor: hasImage ? 'pointer' : 'default' }}
              title={isArmed ? 'Click again to open this sight' : item.label}
              onClick={() => {
                if (hasImage) handleSightCardClick(item, key);
              }}
            >
              <div className="sight-card-thumbnail-container slider-card-thumbnail-container">
                {hasImage ? (
                  <img
                    src={item.imageUrl}
                    alt={item.label}
                    className="sight-card-image slider-card-image"
                  />
                ) : (
                  <div className="sight-card-loading-frame" aria-label={`Loading ${item.label}`}>
                    {isEnriching ? <div className="sight-card-loading-shimmer" /> : null}
                  </div>
                )}
                <div className="sight-card-title-overlay slider-card-title-overlay">
                  <strong>{item.label}</strong>
                  {isArmed ? <span className="sight-card-armed-badge">Click again to set as topic</span> : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
